(() => {
  const elements = (root, selector) => Array.from(root.querySelectorAll(selector));
  const skuToastTimers = new WeakMap();

  function fitBrandName(card) {
    const name = card.querySelector('[data-product-brand-name]');
    const words = elements(name || card, '.le-native-pdp__brand-word');
    if (!name || !words.length) return;

    name.style.removeProperty('--le-pdp-brand-fitted-size');
    const cardStyle = getComputedStyle(card);
    const availableWidth = card.clientWidth
      - Number.parseFloat(cardStyle.paddingInlineStart || '0')
      - Number.parseFloat(cardStyle.paddingInlineEnd || '0');
    const baseFontSize = Number.parseFloat(getComputedStyle(name).fontSize || '44');
    const widestWord = Math.max(...words.map((word) => word.scrollWidth));

    if (widestWord <= availableWidth) return;
    const fittedFontSize = Math.max(18, Math.floor((baseFontSize * availableWidth / widestWord) * 10) / 10);
    name.style.setProperty('--le-pdp-brand-fitted-size', `${fittedFontSize}px`);
  }

  function bindBrandCard(card) {
    if (card.dataset.productBrandFitBound) return;
    card.dataset.productBrandFitBound = 'true';
    const fit = () => fitBrandName(card);
    fit();
    document.fonts?.ready.then(fit);
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(fit);
      observer.observe(card);
    }
  }

  async function copyText(value) {
    if (!value) return false;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }

    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    return copied;
  }

  function showSkuToast(button, message) {
    const section = button.closest('.le-native-pdp');
    const toast = section?.querySelector('[data-sku-copy-toast]');
    if (!toast) return;

    const activeTimer = skuToastTimers.get(toast);
    if (activeTimer) window.clearTimeout(activeTimer);
    toast.textContent = message;
    toast.hidden = false;
    skuToastTimers.set(toast, window.setTimeout(() => {
      toast.hidden = true;
      skuToastTimers.delete(toast);
    }, 2200));
  }

  function bindSkuCopy(button) {
    if (button.dataset.copySkuBound) return;
    button.dataset.copySkuBound = 'true';
    button.addEventListener('click', async () => {
      const value = button.dataset.copyValue || '';
      if (!value) return;

      try {
        const copied = await copyText(value);
        if (!copied) return;
        const defaultLabel = button.dataset.copyLabel || '';
        const copiedLabel = button.dataset.copiedLabel || defaultLabel;
        button.setAttribute('aria-label', copiedLabel);
        button.setAttribute('title', copiedLabel);
        showSkuToast(button, copiedLabel);
        window.setTimeout(() => {
          button.setAttribute('aria-label', defaultLabel);
          button.setAttribute('title', defaultLabel);
        }, 1500);
      } catch (_error) {
        // Keep the product code visible and leave the control ready for retry.
      }
    });
  }

  const desktopDescriptionMedia = window.matchMedia('(min-width: 1200px)');

  function renderedLineRects(body) {
    const rects = [];
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.textContent?.trim()) continue;
      const range = document.createRange();
      range.selectNodeContents(node);
      Array.from(range.getClientRects()).forEach((rect) => {
        if (rect.width > 0 && rect.height > 0) rects.push(rect);
      });
      range.detach();
    }

    rects.sort((left, right) => left.top - right.top || left.left - right.left);
    return rects.reduce((lines, rect) => {
      const previous = lines[lines.length - 1];
      if (!previous || Math.abs(previous.top - rect.top) > 1) {
        lines.push({ top: rect.top, bottom: rect.bottom });
      } else {
        previous.bottom = Math.max(previous.bottom, rect.bottom);
      }
      return lines;
    }, []);
  }

  function measureDescription(container) {
    if (container.dataset.descriptionExpanded === 'true') return;
    const body = container.querySelector('[data-product-description-body]');
    const button = container.querySelector('[data-product-description-more]');
    if (!body || !button || body.getClientRects().length === 0) return;

    body.dataset.descriptionState = 'measuring';
    body.style.removeProperty('--le-pdp-description-collapsed-height');
    button.hidden = true;
    const lines = renderedLineRects(body);
    const lineLimit = desktopDescriptionMedia.matches ? 10 : 20;
    container.dataset.descriptionRenderedLines = String(lines.length);
    container.dataset.descriptionLineLimit = String(lineLimit);

    if (lines.length <= lineLimit) {
      body.dataset.descriptionState = 'complete';
      button.setAttribute('aria-expanded', 'true');
      return;
    }

    const bodyRect = body.getBoundingClientRect();
    const collapsedHeight = Math.max(0, lines[lineLimit - 1].bottom - bodyRect.top);
    body.style.setProperty('--le-pdp-description-collapsed-height', `${collapsedHeight}px`);
    body.dataset.descriptionState = 'collapsed';
    button.hidden = false;
    button.setAttribute('aria-expanded', 'false');
  }

  function bindDescription(container) {
    if (container.dataset.productDescriptionBound) return;
    container.dataset.productDescriptionBound = 'true';
    const body = container.querySelector('[data-product-description-body]');
    const button = container.querySelector('[data-product-description-more]');
    if (!body || !button) return;

    const measure = () => window.requestAnimationFrame(() => measureDescription(container));
    button.addEventListener('click', () => {
      container.dataset.descriptionExpanded = 'true';
      body.dataset.descriptionState = 'expanded';
      body.style.removeProperty('--le-pdp-description-collapsed-height');
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
    });

    const details = container.closest('details');
    details?.addEventListener('toggle', () => {
      if (details.open) measure();
    });

    let measuredWidth = 0;
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width || 0;
        if (Math.abs(width - measuredWidth) < 1) return;
        measuredWidth = width;
        measure();
      });
      observer.observe(container);
    }

    measure();
    document.fonts?.ready.then(measure);
  }

  function activateMedia(section, mediaId, shouldScroll = false) {
    if (!section || !mediaId) return;

    elements(section, '[data-product-media-item]').forEach((item) => {
      item.classList.toggle('is-active', String(item.dataset.productMediaId) === String(mediaId));
    });
    elements(section, '[data-product-gallery-thumb]').forEach((thumb) => {
      if (String(thumb.dataset.productMediaTarget) === String(mediaId)) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (!shouldScroll || !window.matchMedia('(max-width: 989px)').matches) return;
    const gallery = section.querySelector('[data-product-gallery]');
    const media = section.querySelector(`[data-product-media-id="${mediaId}"]`);
    if (gallery && media) gallery.scrollTo({ left: media.offsetLeft, behavior: 'smooth' });
  }

  function initialize(root = document) {
    elements(root, '.le-native-pdp').forEach((section) => {
      elements(section, '.le-native-pdp__brand-card').forEach(bindBrandCard);
      elements(section, '[data-copy-sku]').forEach(bindSkuCopy);
      elements(section, '[data-product-description]').forEach(bindDescription);

      if (!section.dataset.productMediaSelectionBound) {
        section.dataset.productMediaSelectionBound = 'true';
        section.addEventListener('letoile:product-media:select', (event) => {
          activateMedia(section, event.detail?.mediaId, true);
        });
      }

      elements(section, '[data-product-gallery-thumbs]').forEach((list) => {
        if (list.dataset.productMediaBound) return;
        list.dataset.productMediaBound = 'true';
        list.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-product-gallery-thumb]');
          if (!thumb || !list.contains(thumb)) return;
          event.preventDefault();

          if (thumb.hasAttribute('data-product-gallery-overflow')) {
            const overflowItems = list.querySelector('[data-product-gallery-overflow-items]');
            if (overflowItems?.content) {
              const firstRevealedThumb = overflowItems.content.querySelector('[data-product-gallery-thumb]');
              const firstRevealedMediaId = firstRevealedThumb?.dataset.productMediaTarget;
              thumb.replaceWith(overflowItems.content);
              overflowItems.remove();
              activateMedia(section, firstRevealedMediaId || thumb.dataset.productMediaTarget, true);
              firstRevealedThumb?.focus({ preventScroll: true });
              return;
            }
          }

          activateMedia(section, thumb.dataset.productMediaTarget, true);
        });
      });

      elements(section, '[data-product-gallery]').forEach((gallery) => {
        if (gallery.dataset.productGalleryObserved || typeof IntersectionObserver !== 'function') return;
        gallery.dataset.productGalleryObserved = 'true';
        const observer = new IntersectionObserver((entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
          activateMedia(section, visible?.target?.dataset.productMediaId);
        }, { root: gallery, threshold: [0.6] });
        elements(gallery, '[data-product-media-item]').forEach((item) => observer.observe(item));
      });
    });
  }

  initialize();
  if (!window.LetoileProductMediaInitialized) {
    window.LetoileProductMediaInitialized = true;
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    desktopDescriptionMedia.addEventListener('change', () => {
      elements(document, '[data-product-description]').forEach((container) => {
        if (container.dataset.descriptionExpanded !== 'true') measureDescription(container);
      });
    });
  }
})();
